// @ts-nocheck
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { URL } from '../../service/Service';
import { CheckCircleIcon, EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/react/20/solid';
import AuthShell from '../../components/auth/AuthShell';
import { getRoleHome } from '../../components/RoleRoute';

export default function LoginPage() {

  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [error, setError] = useState('');
  const [isRememberMeChecked, setIsRememberMeChecked] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const history = useNavigate();
  const location = useLocation();
  const [registrationSuccess, setRegistrationSuccess] = useState(
    Boolean(location.state?.registrationSuccess)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const loginResponse = await axios.post(`${URL}/api/authenticate`, {
        userEmail: userEmail,
        userPassword: userPassword,
        ...(twoFactorRequired && !useRecoveryCode ? { twoFactorCode } : {}),
        ...(twoFactorRequired && useRecoveryCode ? { recoveryCode: twoFactorCode } : {}),
      });
      if (loginResponse.status === 202 || loginResponse.data?.twoFactorRequired) {
        setTwoFactorRequired(true);
        setTwoFactorCode('');
        setError('');
        return;
      }
      sessionStorage.setItem('jwt', loginResponse.data);
      if (isRememberMeChecked) {
        localStorage.setItem("userEmail", userEmail);
      }
      const decoded = jwtDecode(loginResponse.data);
      const role = decoded.authorities[0].authority;
      history(getRoleHome(role));
    } catch (error) {
      const blocked = error?.response?.status === 423 || error?.response?.data?.code === 'ACCOUNT_BLOCKED';
      setError(blocked
        ? (error?.response?.data?.message || 'A sua conta encontra-se bloqueada. Contacte a administração da plataforma.')
        : twoFactorRequired ? 'Código de autenticação inválido.' : 'Email ou palavra-passe inválidos.');
    }
  };

  useEffect(() => {
    const registeredEmail = location.state?.registeredEmail;
    if (registeredEmail) {
      setUserEmail(registeredEmail);
    } else if (localStorage.getItem("userEmail")) {
      setUserEmail(localStorage.getItem("userEmail"));
    }

    if (location.state?.registrationSuccess) {
      window.history.replaceState({}, document.title, '/login');
    }
  }, [location.state]);

  return (
    <AuthShell title="Bem-vindo de volta" description="Entre para gerir os seus formulários, respostas e pagamentos.">
            {registrationSuccess && (
              <div className="registration-success-backdrop" role="presentation">
                <section className="registration-success-modal" role="dialog" aria-modal="true" aria-labelledby="registration-success-title">
                  <button
                    type="button"
                    className="registration-success-close"
                    onClick={() => setRegistrationSuccess(false)}
                    aria-label="Fechar mensagem"
                  >
                    <XMarkIcon />
                  </button>
                  <div className="registration-success-icon"><CheckCircleIcon /></div>
                  <p className="registration-success-eyebrow">Registo concluído</p>
                  <h2 id="registration-success-title">Conta criada com sucesso!</h2>
                  <p>O seu acesso à plataforma Formulários está pronto. Já pode iniciar sessão com o email e a palavra-passe que acabou de definir.</p>
                  <button type="button" className="ui-button-primary w-full" onClick={() => setRegistrationSuccess(false)}>
                    Continuar para o login
                  </button>
                </section>
              </div>
            )}
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="ui-label">
                  Email
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={userEmail}
                    onChange={e => setUserEmail(e.target.value)}
                    className="ui-input"
                    placeholder="nome@empresa.pt"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="ui-label">
                  Palavra-passe
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={passwordVisible ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={userPassword}
                    onChange={e => setUserPassword(e.target.value)}
                    className="ui-input pr-12"
                    placeholder="Introduza a sua palavra-passe"
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-green-cl hover:bg-emerald-50"
                    aria-label={passwordVisible ? 'Ocultar palavra-passe' : 'Mostrar palavra-passe'}
                  >
                    {passwordVisible ? <EyeSlashIcon className="h-5 w-5 text-gray-500" /> : <EyeIcon className="h-5 w-5 text-gray-500" />}
                  </button>
                </div>
              </div>
              {twoFactorRequired && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
                  <label htmlFor="two-factor-code" className="ui-label">
                    {useRecoveryCode ? 'Código de recuperação' : 'Código de autenticação'}
                  </label>
                  <input
                    id="two-factor-code"
                    type="text"
                    inputMode={useRecoveryCode ? 'text' : 'numeric'}
                    autoComplete="one-time-code"
                    required
                    autoFocus
                    maxLength={useRecoveryCode ? 9 : 6}
                    value={twoFactorCode}
                    onChange={(event) => setTwoFactorCode(useRecoveryCode
                      ? event.target.value.toUpperCase()
                      : event.target.value.replace(/\D/g, ''))}
                    className="ui-input mt-2 text-center text-lg font-bold tracking-[0.28em]"
                    placeholder={useRecoveryCode ? 'XXXX-XXXX' : '000000'}
                  />
                  <button
                    type="button"
                    className="auth-link mt-3 text-xs"
                    onClick={() => {
                      setUseRecoveryCode((current) => !current);
                      setTwoFactorCode('');
                      setError('');
                    }}
                  >
                    {useRecoveryCode ? 'Usar aplicação Authenticator' : 'Usar código de recuperação'}
                  </button>
                </div>
              )}
              {error && <div className="ui-alert-error text-center">{error}</div>}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={isRememberMeChecked}
                    onChange={() => setIsRememberMeChecked(!isRememberMeChecked)}
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Lembrar email
                  </label>
                </div>

                <div className="text-sm">
                  <Link to="/resetpassword" className="auth-link">
                    Esqueceu-se da palavra-passe?
                  </Link>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  className="ui-button-primary w-full"
                >
                  {twoFactorRequired ? 'Confirmar código' : 'Entrar'}
                </button>
 
                <button
                  type="button"
                  onClick={() => history('/register')}
                  className="ui-button-secondary mt-3 w-full"
                >
                  Registar
                </button>
              </div>
              <div className="text-sm text-center">
                <Link to="/" className="auth-link">
                  Voltar à Página Inicial
                </Link>
              </div>
            </form>
    </AuthShell>
  )
}
